import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import ProjectsAdmin from "./admin/ProjectsAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-US", {
            month: "short", day: "numeric", year: "numeric",
            hour: "numeric", minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const toCsv = (rows) => {
    const cols = ["created_at", "source", "name", "email", "phone", "project_type", "message", "contacted"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = cols.join(",");
    const body = rows.map((r) => cols.map((c) => esc(r[c])).join(",")).join("\n");
    return header + "\n" + body;
};

const StatCard = ({ label, value, testid }) => (
    <div data-testid={testid} className="bg-card border border-border p-6">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">{label}</div>
        <div className="font-display font-black text-4xl text-foreground">{value ?? "—"}</div>
    </div>
);

const LoginView = ({ onSuccess }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        try {
            await axios.post(`${API}/auth/login`, { email, password });
            onSuccess();
        } catch (e2) {
            const d = e2.response?.data?.detail;
            setErr(typeof d === "string" ? d : "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-6">
            <form
                onSubmit={submit}
                data-testid="admin-login-form"
                className="w-full max-w-sm bg-card border border-border p-8"
            >
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-accent mb-3">Admin</div>
                <h1 className="font-display font-black text-3xl uppercase tracking-tight mb-8">Sign in.</h1>

                <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-1">Email</label>
                <input
                    data-testid="admin-email-input"
                    type="email" required autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-background border border-border px-3 py-2 mb-5 focus:outline-none focus:border-accent"
                />

                <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-1">Password</label>
                <input
                    data-testid="admin-password-input"
                    type="password" required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-background border border-border px-3 py-2 mb-6 focus:outline-none focus:border-accent"
                />

                {err && (
                    <div data-testid="admin-login-error" className="text-sm text-red-600 mb-4">{err}</div>
                )}

                <button
                    data-testid="admin-login-submit"
                    type="submit" disabled={busy}
                    className="w-full bg-accent text-accent-foreground py-3 font-bold uppercase tracking-wider hover:bg-foreground transition-colors disabled:opacity-60"
                >
                    {busy ? "Signing in…" : "Sign in"}
                </button>
            </form>
        </div>
    );
};

const RANGE_LABELS = [
    ["hour", "Last hour"],
    ["day", "Last day"],
    ["week", "Last week"],
    ["month", "Last month"],
    ["3m", "3 months"],
    ["6m", "6 months"],
    ["year", "Year"],
    ["all", "All time"],
];

const VisitsPanel = () => {
    const [range, setRange] = useState("week");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (r) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/visits`, { params: { range: r } });
            setData(data);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(range); }, [range, load]);

    return (
        <section data-testid="visits-panel" className="mb-10">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
                <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-accent mb-1">Traffic</div>
                    <div className="font-display font-black text-2xl uppercase tracking-tight">Site visits</div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {RANGE_LABELS.map(([k, label]) => (
                        <button
                            key={k}
                            data-testid={`range-${k}`}
                            onClick={() => setRange(k)}
                            className={`font-mono text-[10px] uppercase tracking-[0.25em] border px-3 py-2 transition-colors ${
                                range === k
                                    ? "border-accent text-accent"
                                    : "border-border text-muted-foreground hover:border-foreground"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard testid="visits-total" label="Page views" value={loading ? "…" : data?.total_views ?? 0} />
                <StatCard testid="visits-unique" label="Unique visitors" value={loading ? "…" : data?.unique_visitors ?? 0} />
                <div data-testid="visits-toppaths" className="bg-card border border-border p-6">
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">Top pages</div>
                    {loading && <div className="text-muted-foreground text-sm">Loading…</div>}
                    {!loading && (!data?.top_paths || data.top_paths.length === 0) && (
                        <div className="text-muted-foreground text-sm italic">No visits in this range yet.</div>
                    )}
                    {!loading && data?.top_paths?.slice(0, 5).map((row) => (
                        <div key={row.path} className="flex items-center justify-between py-1 text-sm">
                            <span className="truncate font-mono text-xs">{row.path}</span>
                            <span className="font-bold ml-3">{row.count}</span>
                        </div>
                    ))}
                </div>
                <div data-testid="visits-sources" className="bg-card border border-border p-6">
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">Traffic sources</div>
                    {loading && <div className="text-muted-foreground text-sm">Loading…</div>}
                    {!loading && (!data?.sources || data.sources.length === 0) && (
                        <div className="text-muted-foreground text-sm italic">No visits in this range yet.</div>
                    )}
                    {!loading && data?.sources?.slice(0, 6).map((row) => (
                        <div key={row.source} data-testid={`source-${row.source.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="py-1.5">
                            <div className="flex items-center justify-between text-sm">
                                <span className="truncate">{row.source}</span>
                                <span className="font-bold ml-3 font-mono text-xs">{row.pct}% <span className="text-muted-foreground font-normal">({row.count})</span></span>
                            </div>
                            <div className="h-1 bg-border mt-1.5">
                                <div className="h-1 bg-accent" style={{ width: `${row.pct}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const Dashboard = ({ email, onLogout }) => {
    const [tab, setTab] = useState("leads");
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [s, l] = await Promise.all([
                axios.get(`${API}/admin/stats`),
                axios.get(`${API}/admin/leads`),
            ]);
            setStats(s.data);
            setLeads(l.data);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const toggleContacted = async (lead) => {
        const nextVal = !lead.contacted;
        setLeads((prev) => prev.map((r) => (r.id === lead.id ? { ...r, contacted: nextVal } : r)));
        try {
            await axios.patch(`${API}/admin/leads/${lead.id}`, { contacted: nextVal });
            fetchAll();
        } catch {
            setLeads((prev) => prev.map((r) => (r.id === lead.id ? { ...r, contacted: !nextVal } : r)));
        }
    };

    const remove = async (lead) => {
        if (!window.confirm(`Delete lead from ${lead.email}?`)) return;
        try {
            await axios.delete(`${API}/admin/leads/${lead.id}`);
            fetchAll();
        } catch { /* noop */ }
    };

    const logout = async () => {
        try { await axios.post(`${API}/auth/logout`); } catch { /* noop */ }
        onLogout();
    };

    const download = () => {
        const csv = toCsv(filtered);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mwc-leads-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const filtered = leads.filter((l) => {
        if (filter === "all") return true;
        if (filter === "hero") return l.source === "hero_cta";
        if (filter === "quote") return l.source === "quote_form";
        if (filter === "new") return !l.contacted;
        if (filter === "contacted") return l.contacted;
        return true;
    });

    return (
        <div className="min-h-screen bg-background text-foreground">
            <header className="border-b border-border bg-card">
                <div className="px-6 md:px-12 py-6 flex items-center justify-between">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-accent">MWC Admin</div>
                        <div className="font-display font-black text-2xl uppercase tracking-tight">Dashboard</div>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="font-mono text-xs text-muted-foreground hidden sm:inline">{email}</span>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={logout}
                            className="font-mono text-xs uppercase tracking-[0.25em] border border-border px-4 py-2 hover:border-accent hover:text-accent transition-colors"
                        >
                            Log out
                        </button>
                    </div>
                </div>
                <nav className="px-6 md:px-12 border-t border-border flex gap-1">
                    {[
                        ["leads", "Leads"],
                        ["projects", "Edit Site — Projects"],
                    ].map(([k, label]) => (
                        <button
                            key={k}
                            data-testid={`tab-${k}`}
                            onClick={() => setTab(k)}
                            className={`font-mono text-[11px] uppercase tracking-[0.25em] px-4 py-3 border-b-2 transition-colors ${
                                tab === k
                                    ? "border-accent text-accent"
                                    : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </nav>
            </header>

            <main className="px-6 md:px-12 py-10">
                {tab === "projects" && <ProjectsAdmin />}
                {tab === "leads" && (
                    <>
                <VisitsPanel />
                {/* STATS */}
                <div data-testid="admin-stats-grid" className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
                    <StatCard testid="stat-total" label="Total leads" value={stats?.total} />
                    <StatCard testid="stat-last7" label="Last 7 days" value={stats?.last_7_days} />
                    <StatCard testid="stat-new" label="Uncontacted" value={stats ? stats.total - stats.contacted : "—"} />
                    <StatCard testid="stat-hero" label="Bid signups" value={stats?.hero_signups} />
                    <StatCard testid="stat-quote" label="Quote requests" value={stats?.quote_requests} />
                </div>

                {/* CONTROLS */}
                <div className="flex flex-wrap items-center gap-3 mb-6">
                    <div className="flex flex-wrap gap-2">
                        {[
                            ["all", "All"],
                            ["new", "New"],
                            ["contacted", "Contacted"],
                            ["hero", "Bid signups"],
                            ["quote", "Quote requests"],
                        ].map(([k, label]) => (
                            <button
                                key={k}
                                data-testid={`filter-${k}`}
                                onClick={() => setFilter(k)}
                                className={`font-mono text-[11px] uppercase tracking-[0.25em] border px-3 py-2 transition-colors ${
                                    filter === k
                                        ? "border-accent text-accent"
                                        : "border-border text-muted-foreground hover:border-foreground"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="ml-auto flex gap-2">
                        <button
                            data-testid="admin-refresh-btn"
                            onClick={fetchAll}
                            className="font-mono text-[11px] uppercase tracking-[0.25em] border border-border px-3 py-2 hover:border-accent hover:text-accent"
                        >
                            Refresh
                        </button>
                        <button
                            data-testid="admin-export-btn"
                            onClick={download}
                            className="font-mono text-[11px] uppercase tracking-[0.25em] bg-accent text-accent-foreground px-3 py-2 hover:bg-foreground"
                        >
                            Export CSV
                        </button>
                    </div>
                </div>

                {/* LEADS TABLE */}
                <div className="border border-border overflow-x-auto">
                    <table data-testid="admin-leads-table" className="w-full text-sm">
                        <thead className="bg-card border-b border-border">
                            <tr className="text-left font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                                <th className="p-3">Received</th>
                                <th className="p-3">Source</th>
                                <th className="p-3">Name</th>
                                <th className="p-3">Email</th>
                                <th className="p-3">Phone</th>
                                <th className="p-3">Details</th>
                                <th className="p-3 text-center">Contacted</th>
                                <th className="p-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No leads match this filter.</td></tr>
                            )}
                            {filtered.map((l) => (
                                <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-border/60 last:border-b-0 align-top">
                                    <td className="p-3 whitespace-nowrap font-mono text-xs">{formatDate(l.created_at)}</td>
                                    <td className="p-3">
                                        <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${
                                            l.source === "hero_cta" ? "border-accent text-accent" : "border-border text-muted-foreground"
                                        }`}>
                                            {l.source === "hero_cta" ? "Bid" : l.source === "quote_form" ? "Quote" : l.source || "—"}
                                        </span>
                                    </td>
                                    <td className="p-3">{l.name || "—"}</td>
                                    <td className="p-3">
                                        <a href={`mailto:${l.email}`} className="hover:text-accent">{l.email}</a>
                                    </td>
                                    <td className="p-3 whitespace-nowrap">
                                        {l.phone ? <a href={`tel:${l.phone}`} className="hover:text-accent">{l.phone}</a> : "—"}
                                    </td>
                                    <td className="p-3 max-w-xs">
                                        {l.project_type && (
                                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{l.project_type}</div>
                                        )}
                                        <div className="text-xs leading-relaxed">{l.message || "—"}</div>
                                    </td>
                                    <td className="p-3 text-center">
                                        <button
                                            data-testid={`toggle-contacted-${l.id}`}
                                            onClick={() => toggleContacted(l)}
                                            className={`w-5 h-5 border-2 ${l.contacted ? "bg-accent border-accent" : "border-border"} inline-flex items-center justify-center`}
                                            aria-label="toggle contacted"
                                        >
                                            {l.contacted && <span className="text-accent-foreground text-xs leading-none">✓</span>}
                                        </button>
                                    </td>
                                    <td className="p-3 text-right">
                                        <button
                                            data-testid={`delete-lead-${l.id}`}
                                            onClick={() => remove(l)}
                                            className="text-xs text-muted-foreground hover:text-red-600"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <p className="mt-6 text-xs text-muted-foreground">
                    Showing {filtered.length} of {leads.length} leads. Auto-notifications also emailed to daniel@markwilsonconstruction.com when each lead comes in.
                </p>
                    </>
                )}
            </main>
        </div>
    );
};

const Admin = () => {
    const [state, setState] = useState({ status: "checking", email: null });

    const check = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/auth/me`);
            setState({ status: "in", email: data.email });
        } catch {
            setState({ status: "out", email: null });
        }
    }, []);

    useEffect(() => { check(); }, [check]);

    if (state.status === "checking") {
        return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground font-mono text-sm">Loading…</div>;
    }
    if (state.status === "out") {
        return <LoginView onSuccess={check} />;
    }
    return <Dashboard email={state.email} onLogout={() => setState({ status: "out", email: null })} />;
};

export default Admin;
